import { MessageGroup } from '@mangovoice/types'

import {
  areEquivalentPhoneNumbers,
  normalizePhoneForComparison,
} from './phoneNumberComparison'

export interface SmsEligiblePhoneNumber {
  number: string
  name?: string
}

export interface SmsPhoneNumberOption {
  value: string
  label: string
}

const getPrimaryOrFirstPhone = (group: MessageGroup) => {
  return (
    group.phone_numbers?.find((phone) => phone.is_primary_sms === true) ??
    group.phone_numbers?.[0]
  )
}

export const getEligibleMessagingGroups = (
  messagingGroups: MessageGroup[],
  smsEligiblePhoneNumbers: SmsEligiblePhoneNumber[],
): MessageGroup[] => {
  const eligiblePhoneNumberKeys = new Set<string>()

  smsEligiblePhoneNumbers.forEach((phone) => {
    const normalized = normalizePhoneForComparison(phone.number)
    if (normalized) {
      eligiblePhoneNumberKeys.add(normalized)
    }
  })

  return messagingGroups.filter((group) => {
    const primaryPhone = getPrimaryOrFirstPhone(group)
    if (!primaryPhone) return false

    const normalizedPrimary =
      normalizePhoneForComparison(primaryPhone.number) ||
      normalizePhoneForComparison(primaryPhone.number_for_display)

    return Boolean(
      normalizedPrimary && eligiblePhoneNumberKeys.has(normalizedPrimary),
    )
  })
}

export const getAvailablePhoneNumberOptions = (
  eligibleMessagingGroups: MessageGroup[],
  smsEligiblePhoneNumbers: SmsEligiblePhoneNumber[],
): SmsPhoneNumberOption[] => {
  const usedNumbers = new Set<string>()

  eligibleMessagingGroups.forEach((group) => {
    const primaryPhone = getPrimaryOrFirstPhone(group)
    if (!primaryPhone) return

    const normalizedNumber = normalizePhoneForComparison(primaryPhone.number)
    if (normalizedNumber) usedNumbers.add(normalizedNumber)

    const normalizedDisplay = normalizePhoneForComparison(
      primaryPhone.number_for_display,
    )
    if (normalizedDisplay) usedNumbers.add(normalizedDisplay)
  })

  return smsEligiblePhoneNumbers
    .filter((phone) => {
      const normalized = normalizePhoneForComparison(phone.number)
      return Boolean(normalized && !usedNumbers.has(normalized))
    })
    .map((phone) => ({
      value: phone.number,
      label: phone.name ? `${phone.number} (${phone.name})` : phone.number,
    }))
}

export const findMessagingGroupBySendFrom = (
  eligibleMessagingGroups: MessageGroup[],
  value?: string,
): MessageGroup | undefined => {
  if (!value || !eligibleMessagingGroups.length) return undefined

  const byId = eligibleMessagingGroups.find(
    (group) => String(group.id) === String(value),
  )
  if (byId) return byId

  return eligibleMessagingGroups.find((group) => {
    const primaryPhone = getPrimaryOrFirstPhone(group)
    if (!primaryPhone) return false

    return (
      String(primaryPhone.id) === value ||
      areEquivalentPhoneNumbers(primaryPhone.number, value) ||
      areEquivalentPhoneNumbers(primaryPhone.number_for_display, value)
    )
  })
}

export const findPhoneNumberOptionBySendFrom = (
  options: SmsPhoneNumberOption[],
  value?: string,
): SmsPhoneNumberOption | undefined => {
  if (!value) return undefined
  return options.find((option) =>
    areEquivalentPhoneNumbers(option.value, value),
  )
}

export const getFirstSendFromOptionValue = (
  eligibleMessagingGroups: MessageGroup[],
  availablePhoneNumberOptions: SmsPhoneNumberOption[],
): string | undefined => {
  if (eligibleMessagingGroups.length > 0) {
    return String(eligibleMessagingGroups[0].id)
  }

  if (availablePhoneNumberOptions.length > 0) {
    return availablePhoneNumberOptions[0].value
  }

  return undefined
}
